import { useCallback, useEffect, useState } from 'react';

import type { MusicGenre, RankingResponse } from '../types';
import { useMusicGenreOptions } from '../lib/music-genres';
import { SponsoredSlot } from './SponsoredSlot';

const countries = [
  ['', 'Todos los países'],
  ['EC', 'Ecuador'],
  ['AR', 'Argentina'],
  ['BO', 'Bolivia'],
  ['BR', 'Brasil'],
  ['CL', 'Chile'],
  ['CO', 'Colombia'],
  ['CR', 'Costa Rica'],
  ['DO', 'República Dominicana'],
  ['ES', 'España'],
  ['GT', 'Guatemala'],
  ['HN', 'Honduras'],
  ['MX', 'México'],
  ['PA', 'Panamá'],
  ['PE', 'Perú'],
  ['PR', 'Puerto Rico'],
  ['US', 'Estados Unidos'],
  ['UY', 'Uruguay'],
  ['VE', 'Venezuela'],
] as const;

const PAGE_SIZE = 10;

export function RankingBoard() {
  const genres = useMusicGenreOptions();
  const [genre, setGenre] = useState<MusicGenre>('rap');
  const [country, setCountry] = useState('');
  const [offset, setOffset] = useState(0);
  const [ranking, setRanking] = useState<RankingResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const query = new URLSearchParams({
        genre,
        limit: String(PAGE_SIZE),
        offset: String(offset),
      });
      if (country) query.set('country', country);
      const response = await fetch(`/api/v1/rankings/artists?${query}`, {
        headers: { Accept: 'application/json' },
      });
      if (!response.ok) throw new Error('No fue posible cargar el ranking.');
      setRanking((await response.json()) as RankingResponse);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'No fue posible cargar el ranking.');
    } finally {
      setLoading(false);
    }
  }, [country, genre, offset]);

  useEffect(() => {
    void load();
  }, [load]);

  function changeGenre(value: MusicGenre) {
    setGenre(value);
    setOffset(0);
  }

  function changeCountry(value: string) {
    setCountry(value);
    setOffset(0);
  }

  return (
    <section id="ranking" className="mt-24 lg:mt-32">
      <div className="flex flex-wrap items-end justify-between gap-6">
        <div>
          <p className="eyebrow">Ranking público</p>
          <h2 className="mt-3 text-3xl font-black tracking-tight text-white sm:text-5xl">
            El respeto se gana en la arena.
          </h2>
          <p className="mt-4 max-w-2xl text-sm leading-6 text-zinc-400">
            Elo por género: todos comienzan en 1000. Solo cuentan batallas con al menos 10 votos
            válidos; el patrocinio nunca altera una posición.
          </p>
        </div>
        <SponsoredSlot slot="artist_ranking" compact />
      </div>

      <div className="mt-7 grid gap-3 sm:grid-cols-2">
        <label className="field-label">
          Género
          <select
            className="field-input mt-2"
            value={genre}
            onChange={(event) => changeGenre(event.target.value as MusicGenre)}
          >
            {genres.map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </label>
        <label className="field-label">
          País
          <select
            className="field-input mt-2"
            value={country}
            onChange={(event) => changeCountry(event.target.value)}
          >
            {countries.map(([value, label]) => (
              <option key={value || 'all'} value={value}>
                {label}
              </option>
            ))}
          </select>
        </label>
      </div>

      <div className="glass-panel mt-5 overflow-hidden rounded-[2rem]">
        {loading ? (
          <p className="p-8 text-center text-sm text-zinc-500">Calculando posiciones…</p>
        ) : error ? (
          <div className="p-8 text-center">
            <p className="text-sm text-rose-200">{error}</p>
            <button
              type="button"
              onClick={() => void load()}
              className="mt-4 rounded-full border border-white/10 px-4 py-2 text-xs text-zinc-300"
            >
              Reintentar
            </button>
          </div>
        ) : !ranking?.artists.length ? (
          <p className="p-10 text-center text-sm leading-6 text-zinc-500">
            Todavía no hay artistas clasificados en este filtro. Se necesitan batallas finalizadas
            con 10 votos válidos.
          </p>
        ) : (
          <div className="divide-y divide-white/8">
            {ranking.artists.map((artist) => (
              <article
                key={artist.artistId}
                className="grid grid-cols-[3rem_1fr_auto] items-center gap-3 px-5 py-4 sm:grid-cols-[4rem_1fr_8rem_8rem] sm:px-7"
              >
                <span className="text-xl font-black text-fuchsia-200">#{artist.rank}</span>
                <div>
                  <strong className="block text-sm text-white">{artist.artisticName}</strong>
                  <span className="mt-1 block text-[0.65rem] uppercase tracking-wider text-zinc-600">
                    {artist.countryCode} · {artist.ratedBattles} batallas
                  </span>
                </div>
                <span className="hidden text-center text-xs text-zinc-500 sm:block">
                  {artist.record.wins}V · {artist.record.draws}E · {artist.record.losses}D
                </span>
                <strong className="text-right text-lg text-white">{artist.rating} Elo</strong>
              </article>
            ))}
          </div>
        )}
      </div>

      {ranking && ranking.page.total > PAGE_SIZE && (
        <div className="mt-4 flex items-center justify-center gap-3">
          <button
            type="button"
            disabled={offset === 0 || loading}
            onClick={() => setOffset((value) => Math.max(0, value - PAGE_SIZE))}
            className="rounded-full border border-white/10 px-4 py-2 text-xs text-zinc-400 disabled:opacity-30"
          >
            Anteriores
          </button>
          <span className="text-xs text-zinc-600">{ranking.page.total} artistas</span>
          <button
            type="button"
            disabled={!ranking.page.hasMore || loading}
            onClick={() => setOffset((value) => value + PAGE_SIZE)}
            className="rounded-full border border-white/10 px-4 py-2 text-xs text-zinc-400 disabled:opacity-30"
          >
            Siguientes
          </button>
        </div>
      )}
    </section>
  );
}
